import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { useAtom } from 'jotai';
import { Smartphone, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  isMobileHandoffSnoozed,
  mobileHandoffDismissedAtAtom,
} from './continue-on-mobile-card.atoms';
import { continueOnMobileCardMessages } from './continue-on-mobile-card.messages';

/** Lifecycle step the card reports. The app maps it onto `work_surface_actioned`. */
export type ContinueOnMobileCardAction = 'viewed' | 'cta_clicked' | 'dismissed';

export interface ContinueOnMobileCardProps {
  /** Opens the get-mobile surface. The app binds it to the QR modal atom. */
  onGetApp: () => void;
  /** Analytics emit, injected by the app. Fires `viewed` once per mount. */
  onTrack: (action: ContinueOnMobileCardAction) => void;
}

/**
 * Suggests the Heidi mobile app while a Work run waits on the clinician, so the
 * next approval can reach them as a push instead of a chat they have to watch.
 * The X hides it for a week on this device.
 */
export const ContinueOnMobileCard = (props: ContinueOnMobileCardProps) => {
  const [dismissedAt, setDismissedAt] = useAtom(mobileHandoffDismissedAtAtom);
  if (isMobileHandoffSnoozed(dismissedAt)) return null;
  return (
    <ContinueOnMobileCardBody {...props} onDismiss={() => setDismissedAt(Date.now())} />
  );
};

const ContinueOnMobileCardBody = ({
  onGetApp,
  onTrack,
  onDismiss,
}: ContinueOnMobileCardProps & { onDismiss: () => void }) => {
  const intl = useIntl();
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    onTrack('viewed');
  }, [onTrack]);

  return (
    <div
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface p-3 duration-300 animate-in fade-in"
      data-testid="continue-on-mobile-card"
    >
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-accent"
        aria-hidden
      >
        <Smartphone className="size-4 text-text-primary" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <TypographyP2Bold className="text-text-primary">
          {intl.formatMessage(continueOnMobileCardMessages.title)}
        </TypographyP2Bold>
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(continueOnMobileCardMessages.subtitle)}
        </TypographyCaption>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          size="sm"
          onClick={() => {
            onTrack('cta_clicked');
            onGetApp();
          }}
          data-testid="continue-on-mobile-card-get-app"
        >
          {intl.formatMessage(continueOnMobileCardMessages.getTheApp)}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          iconOnly
          aria-label={intl.formatMessage(continueOnMobileCardMessages.dismiss)}
          onClick={() => {
            onTrack('dismissed');
            onDismiss();
          }}
          data-testid="continue-on-mobile-card-dismiss"
        >
          <X />
        </Button>
      </div>
    </div>
  );
};
